/**
 * Land-use section. Three independently-rendered cards:
 *
 *   ClassificationCard  — what the location IS (WorldCover class)
 *   LandUseChangeCard   — how it's evolved (treecover % + EUDR + loss histogram)
 *   ForestStandCard     — only visible when the click resolves to forest:
 *                         biomass / carbon (baked) + canopy height (extras)
 *
 * Each card receives its slice of the data; the parent ExplorePage owns
 * the fetches (combinedPoint/Zone for the baked data, /landuse/extras
 * separately for the slow upstream call).
 */

import { Leaf, ScanLine, TreePine } from "lucide-react";

import type {
  CanopyHeight, ForestChange, LayerStats, LayerValue,
} from "../types/api";

const FOREST_CLASS_IDS = new Set([10, 95]); // WorldCover Tree cover + Mangroves

// `LayerValue[] | LayerStats[]` (union of arrays) trips up generics — a
// generic <T extends LayerValue | LayerStats>(rows: T[], …) can't pick a
// single T from a value typed as the union. Widening to `Row[]` (an array
// of either-type elements) accepts both LayerValue[] and LayerStats[] at
// every callsite and lets `findRow` return a plain union without a
// generic. Type-narrowing for the downstream casts happens via
// `isStatsArray` on the array.
type Row = LayerValue | LayerStats;
type Rows = Row[];

function isStatsArray(rows: Rows): rows is LayerStats[] {
  return rows.length > 0 && "mean" in rows[0];
}

function findRow(rows: Rows, code: string): Row | null {
  return rows.find((r) => r.code === code) ?? null;
}

function fmt(n: number | null | undefined, digits = 2): string {
  return n == null ? "—" : n.toFixed(digits);
}


// ─── Classification ─────────────────────────────────────────────────────

export function ClassificationCard({ rows, modal = false }: { rows: Rows; modal?: boolean }) {
  const wc = findRow(rows, "worldcover");
  if (!wc) return null;
  const label = isStatsArray(rows)
    ? (wc as LayerStats).mode_label
    : (wc as LayerValue).label;
  const value = isStatsArray(rows)
    ? (wc as LayerStats).mode
    : (wc as LayerValue).value;
  if (label == null) return null;
  return (
    <div className="rounded-xs border-[1.2px] border-mef-separator bg-white p-4 shadow-mef-card">
      <div className="mb-3 flex items-center gap-2">
        <ScanLine className="h-4 w-4 text-mef-orange" />
        <h3 className="mef-caption">
          Classification{modal && " — dominant in zone"}
        </h3>
      </div>
      <dl className="space-y-2">
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-[11px] uppercase tracking-[0.04em] text-black/40">
            ESA WorldCover · 2021
          </dt>
          <dd className="flex items-baseline gap-2">
            <span className="text-[14px] font-medium leading-5 text-black/80">{label}</span>
            {value != null && (
              <span className="font-mono text-[10px] tabular-nums text-black/40">
                [{Math.round(value)}]
              </span>
            )}
          </dd>
        </div>
      </dl>
    </div>
  );
}


// ─── Land-use change ────────────────────────────────────────────────────

function ForestChangeBars({ entries, dataEndYear }: { entries: ForestChange["loss_by_year"]; dataEndYear: number }) {
  if (!entries.length) {
    return (
      <div className="mt-3 text-[12px] text-black/40">
        No deforestation events recorded inside this polygon through {dataEndYear}.
      </div>
    );
  }
  const maxHa = Math.max(...entries.map((e) => e.ha));
  return (
    <div className="mt-3 space-y-1.5">
      {entries.map((e) => {
        const pct = (e.ha / maxHa) * 100;
        const isPostEudr = e.year > 2020;
        return (
          <div key={e.year} className="flex items-center gap-2 text-[12px]">
            <span className="w-10 font-mono tabular-nums text-black/60">{e.year}</span>
            <div className="relative h-3 flex-1 overflow-hidden rounded-xs bg-mef-surface-2">
              <div
                className={[
                  "absolute inset-y-0 left-0 rounded-xs",
                  isPostEudr ? "bg-mef-orange" : "bg-mef-green/70",
                ].join(" ")}
                style={{ width: `${Math.max(2, pct)}%` }}
              />
            </div>
            <span className="w-16 text-right font-mono tabular-nums text-black/80">
              {e.ha.toFixed(2)} ha
            </span>
          </div>
        );
      })}
      <div className="pt-1 text-[10px] text-black/40">
        <span className="inline-block h-2 w-2 align-middle bg-mef-orange rounded-xs" /> post-EUDR-cutoff (year &gt; 2020)
        <span className="ml-3 inline-block h-2 w-2 align-middle bg-mef-green/70 rounded-xs" /> pre-cutoff
      </div>
    </div>
  );
}

export function LandUseChangeCard({
  rows, forestChange, mode,
}: {
  rows: Rows;
  forestChange: ForestChange | null | undefined;
  mode: "point" | "zone";
}) {
  const treecover = findRow(rows, "treecover_2000_pct");
  const lossyear = findRow(rows, "lossyear");
  if (!treecover && !lossyear && !forestChange) return null;

  const tcValue = treecover
    ? (isStatsArray(rows)
        ? (treecover as LayerStats).mean
        : (treecover as LayerValue).value)
    : null;
  const lossLabel = lossyear
    ? (isStatsArray(rows)
        ? (lossyear as LayerStats).mode_label
        : (lossyear as LayerValue).label)
    : null;

  return (
    <div className="rounded-xs border-[1.2px] border-mef-separator bg-white p-4 shadow-mef-card">
      <div className="mb-3 flex items-center gap-2">
        <Leaf className="h-4 w-4 text-mef-orange" />
        <h3 className="mef-caption">Land-use change</h3>
      </div>
      <dl className="space-y-2">
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-[11px] uppercase tracking-[0.04em] text-black/40">
            Tree cover · 2000
          </dt>
          <dd className="text-[14px] font-medium tabular-nums text-black/80">
            {fmt(tcValue, 1)}%
          </dd>
        </div>
        {mode === "point" && lossLabel != null && (
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-[11px] uppercase tracking-[0.04em] text-black/40">
              Loss year (this pixel)
            </dt>
            <dd className="text-[14px] font-medium text-black/80">{lossLabel}</dd>
          </div>
        )}
        {forestChange && (
          <>
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-[11px] uppercase tracking-[0.04em] text-black/40">
                Still forested
              </dt>
              <dd className="text-[14px] font-medium tabular-nums text-black/80">
                {forestChange.still_forested_area_ha.toFixed(2)} ha
              </dd>
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-[11px] uppercase tracking-[0.04em] text-black/40">
                Total loss (through {forestChange.data_end_year})
              </dt>
              <dd className="text-[14px] font-medium tabular-nums text-black/80">
                {forestChange.total_loss_area_ha.toFixed(2)} ha
              </dd>
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <dt className={[
                "text-[11px] uppercase tracking-[0.04em]",
                forestChange.post_eudr_loss_area_ha > 0 ? "text-mef-orange" : "text-black/40",
              ].join(" ")}>
                EUDR-relevant (loss &gt; 2020-12-31)
              </dt>
              <dd className={[
                "text-[14px] font-semibold tabular-nums",
                forestChange.post_eudr_loss_area_ha > 0 ? "text-mef-orange" : "text-black/80",
              ].join(" ")}>
                {forestChange.post_eudr_loss_area_ha.toFixed(2)} ha
              </dd>
            </div>
            <ForestChangeBars
              entries={forestChange.loss_by_year}
              dataEndYear={forestChange.data_end_year}
            />
            {forestChange.truncated && (
              <p className="mt-2 text-[10px] text-black/40">
                ⚠ polygon clipped to baked raster extent — totals are a lower bound.
              </p>
            )}
          </>
        )}
      </dl>
    </div>
  );
}


// ─── Forest stand details ───────────────────────────────────────────────

/** Decide whether to show the Forest stand card, based on classification. */
export function isForest(rows: Rows): boolean {
  const wc = findRow(rows, "worldcover");
  if (!wc) return false;
  const id = isStatsArray(rows) ? (wc as LayerStats).mode : (wc as LayerValue).value;
  return id != null && FOREST_CLASS_IDS.has(Math.round(id));
}

export function ForestStandCard({
  rows, canopy, canopyLoading,
}: {
  rows: Rows;
  canopy: CanopyHeight | null;
  canopyLoading: boolean;
}) {
  const biomass = findRow(rows, "biomass_ag_mgha");
  const carbon = findRow(rows, "carbon_ag_tha");
  if (!biomass && !carbon && !canopy && !canopyLoading) return null;

  const biomassValue = biomass
    ? (isStatsArray(rows)
        ? (biomass as LayerStats).mean
        : (biomass as LayerValue).value)
    : null;
  const carbonValue = carbon
    ? (isStatsArray(rows)
        ? (carbon as LayerStats).mean
        : (carbon as LayerValue).value)
    : null;

  return (
    <div className="rounded-xs border-[1.2px] border-mef-separator bg-white p-4 shadow-mef-card">
      <div className="mb-3 flex items-center gap-2">
        <TreePine className="h-4 w-4 text-mef-green" />
        <h3 className="mef-caption">Forest stand</h3>
      </div>
      <dl className="space-y-2">
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-[11px] uppercase tracking-[0.04em] text-black/40">
            Above-ground biomass · 2010
          </dt>
          <dd className="text-[14px] font-medium tabular-nums text-black/80">
            {fmt(biomassValue, 0)} Mg/ha
          </dd>
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-[11px] uppercase tracking-[0.04em] text-black/40">
            Above-ground carbon
          </dt>
          <dd className="text-[14px] font-medium tabular-nums text-black/80">
            {fmt(carbonValue, 1)} tC/ha
          </dd>
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-[11px] uppercase tracking-[0.04em] text-black/40">
            Canopy height
          </dt>
          <dd className="text-[14px] font-medium tabular-nums text-black/80">
            {canopyLoading
              ? <span className="text-black/40">loading…</span>
              : canopy && canopy.mean_m != null
                ? <>{fmt(canopy.mean_m, 1)} m mean <span className="text-[11px] text-black/40">· max {fmt(canopy.max_m, 1)} m</span></>
                : <span className="text-black/40">no canopy data</span>}
          </dd>
        </div>
      </dl>
      <p className="mt-3 text-[10px] text-black/40">
        Biomass: HGB 2010 (Spawn et al.). Carbon = biomass × 0.47 (IPCC default).
        Canopy height: Meta CHM 1 m (Tolan 2024), upstream proxy.
      </p>
    </div>
  );
}
